import { motion } from 'framer-motion';
import { Phone, X } from 'lucide-react';
import { useModal } from '../../hooks/modalContext';
import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { formatPhoneNumber } from '../../utils/utils';
import { isPhoneNumber } from 'class-validator';
import { getMobileAppDownloadUrl } from '../../../auth/_requests';
import { useAuth } from '../../../auth/Auth';
import { showToast } from '../../utils/showToast';
import iosQR from '../../../assets/pages/sideNav/app_store.png';
import androidQR from '../../../assets/pages/sideNav/android_app.png';

const DownloadApp = () => {
  const { setModal } = useModal();
  const { auth } = useAuth();
  const [activeTab, setActiveTab] = useState<'ios' | 'android'>('ios');
  const [isInValid, setInValid] = useState(false);

  const formik = useFormik({
    initialValues: {
      phone: '',
    },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^\d{10}$/, 'Phone number must be 10 digits')
        .required('Phone number is required'),
    }),
    onSubmit: async (values) => {
      if (!values.phone || isInValid) {
        showToast({
          message: 'Please enter a valid phone number',
          type: 'error',
        });
        return;
      }
      try {
        await getMobileAppDownloadUrl(auth?.data?.token, values.phone);
        showToast({
          message: 'App download link sent successfully',
          type: 'success',
        });
      } catch (error) {
        console.error('Error downloading app:', error);
      } finally {
        setModal(null);
      }
    },
  });

  return (
    <motion.div
      className='relative flex max-h-[90vh] w-full max-w-[min(92vw,640px)] flex-col overflow-hidden rounded-[12px] bg-[var(--color-dark)] shadow-xl'
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className='hero shrink-0 !border-none !rounded-none'>
        <div className='pg-hero relative overflow-hidden'>
          <div className='hero-beam' aria-hidden />
          <div className='hero-orb orb-blue' aria-hidden />
          <div className='hero-orb orb-gold' aria-hidden />
          <button
            type='button'
            className='absolute right-4 top-4 z-[2] rounded-full p-1.5 text-[var(--dmuted)] transition-colors hover:bg-white/10 hover:text-white'
            aria-label='Close'
            onClick={() => setModal(null)}
          >
            <X size={22} strokeWidth={2} />
          </button>
          <div className='relative z-[1] px-[24px] py-[24px]  sm:px-8 sm:pb-7'>
            <div className='hero-meta'>
              <div className='badge badge-neutral flex items-center gap-1.5 py-1 pr-2.5 pl-2'>
                <Phone className='h-3.5 w-3.5 shrink-0 opacity-80 text-[#fff]' aria-hidden />
                <span className='font-semibold text-[#fff] uppercase tracking-[0.1em]'>
                  Mobile
                </span>
              </div>
            </div>
            <h2 className='hero-h1 mt-1 text-[clamp(1.25rem,3.5vw,1.6rem)]'>
              Download <em>TenantPay</em>
            </h2>
            <p className='hero-p !mb-0 max-w-[420px]'>
              Get the link by text, or scan the QR code for App Store or Play Store.
            </p>
          </div>
        </div>
      </div>

      <div className='flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto bg-[var(--color-page)] px-5 py-6 sm:px-8'>
        <form onSubmit={formik.handleSubmit} className='flex flex-col gap-2'>
          <span className='label !text-[13px] uppercase font-semibold tracking-[0.54px]'>
            Text me the app
          </span>
          <div className='flex flex-col gap-3 sm:flex-row sm:items-stretch'>
            <div className='inputField flex min-h-[52px] min-w-0 flex-1 flex-row items-center gap-3 !py-0'>
              <Phone className='h-5 w-5 shrink-0 text-medium' aria-hidden />
              <input
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('phone')}
                placeholder='Mobile number'
                className='min-w-0 flex-1 border-0 bg-transparent !p-0'
                maxLength={14}
                value={formatPhoneNumber(formik.values.phone)}
                onChange={(e) => {
                  const formattedPhone = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                  formik.setFieldValue('phone', formattedPhone);
                  if (formattedPhone.length === 10) {
                    if (
                      !isPhoneNumber(formattedPhone, 'CA') &&
                      !isPhoneNumber(formattedPhone, 'US')
                    ) {
                      setInValid(true);
                    } else {
                      setInValid(false);
                      formik.setFieldError('phone', '');
                    }
                  } else {
                    setInValid(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    formik.handleSubmit();
                  }
                }}
              />
            </div>
            <button
              type='submit'
              className='btn-gold flex h-[52px] shrink-0 items-center justify-center px-8 text-[13px] sm:min-w-[200px]'
            >
              Text me the app
            </button>
          </div>

          {formik.touched.phone && formik.errors.phone && (
            <p className='error mt-0.5'>{formik.errors.phone}</p>
          )}
          {formik.touched.phone && isInValid && (
            <p className='error mt-0.5'>Please provide a valid Canadian or US phone number</p>
          )}
        </form>

        <div className='relative flex items-center gap-3 py-0.5'>
          <div className='h-px flex-1 bg-[var(--color-border)]' />
          <span className='font-sans text-[11px] font-medium uppercase tracking-[0.12em] text-light'>
            Or scan
          </span>
          <div className='h-px flex-1 bg-[var(--color-border)]' />
        </div>

        <div className='w-full max-w-[320px] mx-auto'>
          <TabbedQRSelector
            appStoreUrl={iosQR}
            playStoreUrl={androidQR}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
        </div>
      </div>
    </motion.div>
  );
};

export { DownloadApp, TabbedQRSelector };
const TabbedQRSelector = ({
  appStoreUrl,
  playStoreUrl,
  activeTab,
  setActiveTab,
}: {
  appStoreUrl: string;
  playStoreUrl: string;
  activeTab: 'ios' | 'android';
  setActiveTab: (tab: 'ios' | 'android') => void;
}) => {
  return (
    <div className='space-y-4'>
      <div className='flex gap-1 rounded-[10px] border border-[var(--color-border)] bg-white p-1'>
        <button
          type='button'
          onClick={() => setActiveTab('ios')}
          className={`flex-1 rounded-[8px] py-2.5 px-4 font-sans text-[13px] font-semibold transition-all ${
            activeTab === 'ios'
              ? 'bg-[var(--color-dark)] text-white shadow-sm'
              : 'bg-transparent text-medium hover:bg-[var(--color-page)] hover:text-main'
          }`}
        >
          App Store
        </button>
        <button
          type='button'
          onClick={() => setActiveTab('android')}
          className={`flex-1 rounded-[8px] py-2.5 px-4 font-sans text-[13px] font-semibold transition-all ${
            activeTab === 'android'
              ? 'bg-[var(--color-dark)] text-white shadow-sm'
              : 'bg-transparent text-medium hover:bg-[var(--color-page)] hover:text-main'
          }`}
        >
          Play Store
        </button>
      </div>

      <div className='rounded-[12px] border border-[var(--color-border)] bg-white p-5 shadow-sm'>
        <div className='mx-auto flex w-fit flex-col items-center gap-4'>
          <div className='rounded-[10px] border border-[var(--color-border-strong)] bg-white p-3'>
            <img
              src={activeTab === 'ios' ? appStoreUrl : playStoreUrl}
              alt={activeTab === 'ios' ? 'iOS QR Code' : 'Android QR Code'}
              className='h-[200px] w-[200px] object-contain'
            />
          </div>
          <div className='text-center'>
            <p className='font-display text-[1.05rem] font-bold tracking-tight text-main'>
              {activeTab === 'ios' ? 'Download on the App Store' : 'Get it on Google Play'}
            </p>
            <p className='mt-1 font-sans text-[12px] text-medium'>
              Scan to download for {activeTab === 'ios' ? 'iOS' : 'Android'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
